import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../../../styles/theme';

// Keyframes
const pulse = keyframes`
  0% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7); 
  }
  70% { 
    transform: scale(1.05); 
    box-shadow: 0 0 0 12px rgba(40, 167, 69, 0); 
  }
  100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0); 
  }
`;

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Shared button mixin
const buttonMixin = css`
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 12px; // Reduced padding
  font-size: 0.95rem; // Slightly smaller default
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
  white-space: nowrap;
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: bold;
  color: ${theme.colors.primary};
  gap: 10px;
  font-size: 1rem;

  @media (max-width: 992px) {
    font-size: 0.85rem;
    max-width: 60%;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

export const LogoImage = styled.img`
  height: 45px;
  width: auto;

  @media (max-width: 992px) {
    height: 38px;
  }
`;

export const Hamburger = styled.div<{ isOpen: boolean }>`
  display: none;
  flex-direction: column;
  cursor: pointer;
  z-index: 1001;

  div {
    width: 25px;
    height: 2.5px;
    background-color: ${theme.colors.primary};
    margin: 4px 0;
    transition: all 0.3s ease;
    border-radius: 2px;
    transform-origin: center;

    &:first-child {
      transform: ${({ isOpen }) => (isOpen ? 'rotate(45deg) translate(7px, 7px)' : 'rotate(0)')};
    }
    &:nth-child(2) {
      opacity: ${({ isOpen }) => (isOpen ? '0' : '1')};
      transform: ${({ isOpen }) => (isOpen ? 'translateX(-18px)' : 'translateX(0)')};
    }
    &:nth-child(3) {
      transform: ${({ isOpen }) => (isOpen ? 'rotate(-45deg) translate(7px, -7px)' : 'rotate(0)')};
    }
  }

  @media (max-width: 992px) {
    display: flex;
  }
`;

export const NavItem = styled.li`
  position: relative;

  @media (max-width: 992px) {
    width: 100%;
    padding: 0;
    margin-bottom: 0;

    &:not(:last-child) {
      border-bottom: 1px solid #eee;
    }
  }
`;

// Color helper
const getLinkColor = (type?: string) => {
  const map: Record<string, string> = {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    accent: theme.colors.accent,
    success: theme.colors.success,
    warning: theme.colors.warning,
    info: theme.colors.info,
    buy: theme.colors.success,
    renew: theme.colors.warning,
    update: theme.colors.info,
  };
  return map[type || ''] || theme.colors.text;
};

export const NavLink = styled.a<{ $isActive?: boolean; $colorType?: string }>`
  color: ${({ $isActive, $colorType }) => ($isActive ? theme.colors.primary : getLinkColor($colorType))};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 6px 0;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 1.5px;
    background-color: ${theme.colors.primary};
    transform: scaleX(${({ $isActive }) => ($isActive ? 1 : 0)});
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    color: ${theme.colors.primary};
    transform: translateY(-1px);
  }

  &:hover::after {
    transform: scaleX(1);
  }

  @media (max-width: 992px) {
    font-size: 1.1rem;
    padding: 12px 0;
    width: 100%;
    display: block;
    border-bottom: none;

    &::after { display: none; }
    &:hover { 
      background-color: #f5f5f5;
      transform: none;
    }
  }
`;

export const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;

  @media (max-width: 992px) {
    width: 100%;
  }
`;

export const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  list-style: none;
  margin: 6px 0 0;
  padding: 8px 0;
  background-color: white;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  z-index: 10;
  min-width: 160px;

  @media (max-width: 992px) {
    position: static;
    transform: none;
    box-shadow: none;
    background: transparent;
    padding: 0;
    margin-top: 6px;
  }
`;

export const DropdownItem = styled.li`
  @media (max-width: 992px) {
    padding-left: 1.2rem;
  }
`;

export const DropdownLink = styled.a<{ $isActive?: boolean }>`
  display: block;
  padding: 10px 20px;
  text-decoration: none;
  font-size: 0.9rem;
  color: ${theme.colors.text};
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    color: ${theme.colors.primary};
    padding-left: 24px;
  }

  @media (max-width: 992px) {
    font-size: 1rem;
    padding: 10px 0;
    padding-left: 1.2rem;
  }
`;

// Special CTA Button
export const StallNavLink = styled(NavLink)<{ $type: 'buy' | 'renew' | 'update' }>`
  ${({ $type }) => {
    switch ($type) {
      case 'buy':
        return css`
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white !important;
          padding: 8px 16px;
          border-radius: 25px;
          font-weight: 600;
          box-shadow: 0 3px 12px rgba(40, 167, 69, 0.25);
          animation: ${pulse} 2s infinite;

          &:hover {
            transform: translateY(-2px) scale(1.03);
            box-shadow: 0 6px 20px rgba(40, 167, 69, 0.35);
            animation: none;
          }

          @media (max-width: 992px) {
            width: 100%;
            text-align: center;
            font-size: 1.2rem !important;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 14px 0 !important;
            border-radius: 0;
            animation: none;
            position: relative;
            overflow: hidden;

            &::before {
              content: '';
              position: absolute;
              top: 0; left: 0; right: 0; bottom: 0;
              background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
              transform: translateX(-100%);
              animation: ${shimmer} 3s infinite;
            }
          }
        `;

      case 'renew':
        return css`
          background-color: #ffc107;
          color: #212529;
          padding: 8px 16px;
          border-radius: 25px;
          font-weight: 600;

          @media (max-width: 992px) {
            background-color: #ffb300;
            color: white;
            font-size: 1.1rem;
            padding: 14px 0;
            text-align: center;
          }
        `;

      case 'update':
        return css`
          background-color: #17a2b8;
          color: white;
          padding: 8px 16px;
          border-radius: 25px;

          @media (max-width: 992px) {
            font-size: 1.1rem;
            padding: 14px 0;
            text-align: center;
          }
        `;

      default:
        return css``;
    }
  }}

  &::after { display: none; }
`;


export const UserActionsContainer = styled.div`
  display: flex;
  gap: 0.8rem;

  @media (max-width: 992px) {
    flex-direction: column;
    width: 100%;
    padding: 0 1.5rem;
    border-top: 1px solid #eee;
    padding-top: 0.8rem;
    margin-top: 0.8rem;
  }
`;

export const ActionButton = styled.button<{ $colorType?: string }>`
  ${buttonMixin};
  color: ${({ $colorType }) => getLinkColor($colorType)};

  &:hover {
    background-color: ${({ $colorType }) => getLinkColor($colorType) + '12'};
  }

  @media (max-width: 992px) {
    font-size: 1.1rem;
    padding: 12px 0;
    width: 100%;
    justify-content: flex-start;
  }
`;

export const IconWrapper = styled.div<{ $colorType?: string }>`
  display: flex;
  align-items: center;
  color: ${({ $colorType }) => getLinkColor($colorType)};
`;

export const VisitorCount = styled.span`
  font-weight: bold;
  color: #1976d2;
  font-size: 1.1em;
  min-width: 60px;
  text-align: center;
`;

export const VisitorCountLabel = styled.span`
  color: #666;
  font-weight: 500;
  font-size: 0.95em;
`;

// Optional: hide desktop counter on mobile (safety)
export const DesktopOnly = styled.div`
  @media (max-width: 992px) {
    display: none;
  }
`;


export const NavBarContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: ${theme.colors.white};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 1000;

  @media (max-width: 992px) {
    padding: 0.8rem 1rem;
    justify-content: space-between; /* Logo and hamburger only */
  }
`;

export const MobileMenuHeader = styled.div`
  display: none;

  @media (max-width: 992px) {
    display: flex;
    width: 100%;
    padding: 0.8rem 1rem;
    background: ${theme.colors.white};
    align-items: center;
    justify-content: space-between;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1001;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

export const NavList = styled.ul<{ isOpen: boolean }>`
  list-style: none;
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
  gap: 1.8rem;

  /* Always visible and horizontal on desktop */
  @media (min-width: 993px) {
    display: flex;
  }

  /* Hidden by default on mobile, only shown when menu open */
  @media (max-width: 992px) {
    display: none;
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    height: calc(100vh - 70px);
    background: linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%);
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem 1.2rem;
    transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    z-index: 999;
    overflow-y: auto;
    gap: 0;

    /* Show only when isOpen is true */
    ${({ isOpen }) => isOpen && 'display: flex;'}
  }
`;

export const VisitorCounterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #444;

  @media (min-width: 993px) {
    background: transparent;
    padding: 0;
    border: none;
  }

  @media (max-width: 992px) {
    background: white;
    padding: 8px 14px;
    border-radius: 30px;
    border: 1px solid #e1ecf9;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 7px 12px;
    font-size: 12.5px;
  }
`;